import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import moment from "moment";
import Loader from "../Loader";

import { getResults } from "./../../actions/resultActions";
import { toGrade } from "../../utils/examScoring";

import "./ListResults.css";

const EXAM_BADGES = {
  "jamb-utme": { initials: "JU", accent: "#008753" },
  "post-utme": { initials: "PU", accent: "#7c3aed" },
  "wassce-waec-ssce": { initials: "WS", accent: "#2563eb" },
  "jsce-bece": { initials: "JB", accent: "#ea580c" },
};

const ROWS_PER_PAGE_OPTIONS = [10, 20, 30, 50];

function getExamBadge(slug, title) {
  if (EXAM_BADGES[slug]) return EXAM_BADGES[slug];

  const initials = String(title || "Exam")
    .replace(/[^a-zA-Z ]/g, "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join("");

  return { initials: initials || "EX", accent: "#0f766e" };
}

/** WAEC/BECE style exams are reported as letter grades instead of raw scores. */
function isGradedExam(slug) {
  return slug === "wassce-waec-ssce" || slug === "jsce-bece";
}

function getGradeTone(grade) {
  if (grade.startsWith("A") || grade.startsWith("B")) return "blue";
  if (grade.startsWith("C")) return "amber";
  return "red";
}

function getAccuracyBand(percent) {
  if (percent >= 80) return { label: "Good", tone: "green" };
  if (percent >= 65) return { label: "Average", tone: "blue" };
  if (percent >= 50) return { label: "Fair", tone: "amber" };
  return { label: "Weak", tone: "red" };
}

/** `usedtime` is persisted as an "HH:mm:ss" string when the quiz is submitted. */
function formatDuration(usedtime) {
  if (!usedtime) return null;

  const parts = String(usedtime).split(":").map((part) => parseInt(part, 10));
  if (parts.some((part) => Number.isNaN(part))) return null;

  const [hours = 0, minutes = 0, seconds = 0] = parts;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
}

function buildResultRow(quiz, index) {
  const examSlug = quiz?.exam?.slug || "";
  const examTitle = quiz?.exam?.title || "Exam";
  const subjects = Array.isArray(quiz?.subjectId) ? quiz.subjectId.filter(Boolean) : [];
  const subjectNames = subjects.map((subject) => subject?.title).filter(Boolean);

  const questionCount = Number(quiz?.questionCount) || 0;
  const correctCount = Number(quiz?.correctCount) || 0;
  const unseenCount = Number(quiz?.unseenCount) || 0;
  const attemptedCount = Math.max(questionCount - unseenCount, 0);

  const percent = questionCount > 0 ? Math.round((correctCount / questionCount) * 100) : 0;
  const accuracy = attemptedCount > 0 ? Math.round((correctCount / attemptedCount) * 100) : 0;

  const graded = isGradedExam(examSlug);
  const grade = graded ? toGrade(percent) : null;
  const year = String(quiz?.year || "").trim();
  const createdAt = quiz?.createdAt;

  return {
    id: quiz?._id || `result-${index}`,
    examTitle,
    badge: getExamBadge(examSlug, examTitle),
    assessment: quiz?.assessment?.title || "—",
    examType: quiz?.examType ? quiz.examType.charAt(0).toUpperCase() + quiz.examType.slice(1) : "—",
    year: year || null,
    date: createdAt ? new Date(createdAt) : null,
    dateLabel: createdAt ? moment(createdAt).format("DD MMM YYYY") : "—",
    duration: formatDuration(quiz?.usedtime),
    graded,
    grade,
    gradeTone: grade ? getGradeTone(grade) : null,
    score: correctCount,
    questionCount,
    attemptedCount,
    accuracy,
    accuracyBand: getAccuracyBand(accuracy),
    subjectNames,
    subjectLabel:
      subjectNames.length === 0
        ? "—"
        : subjectNames.length === 1
        ? subjectNames[0]
        : `${subjectNames.length} subjects`,
    resultUrl:
      quiz?.examType === "theory"
        ? `/theories-results/${quiz?._id}`
        : `/practice-results/${quiz?._id}`,
  };
}

const ListResults = () => {
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const { rLoading, results } = useSelector((state) => state.results);
  const userId = user?._id;

  const [view, setView] = useState("table");
  const [assessmentFilter, setAssessmentFilter] = useState("all");
  const [examFilter, setExamFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (userId) dispatch(getResults(userId));
  }, [dispatch, userId]);

  const rows = useMemo(() => {
    const quizes = Array.isArray(results?.quizes) ? results.quizes : [];
    return quizes.map(buildResultRow);
  }, [results]);

  const assessmentOptions = useMemo(() => {
    const titles = rows.map((row) => row.assessment).filter((title) => title && title !== "—");
    return Array.from(new Set(titles));
  }, [rows]);

  const examOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.examTitle))),
    [rows]
  );

  const stats = useMemo(() => {
    const attemptsByExam = rows.reduce((acc, row) => {
      acc[row.examTitle] = (acc[row.examTitle] || 0) + 1;
      return acc;
    }, {});

    const mostPractised = Object.entries(attemptsByExam).sort((a, b) => b[1] - a[1])[0];
    const latest = rows.find((row) => row.date);

    return {
      totalAttempts: rows.length,
      examTypes: examOptions.length,
      mostPractised: mostPractised ? `${mostPractised[0]} (${mostPractised[1]})` : "—",
      lastAttempt: latest ? latest.dateLabel : "—",
    };
  }, [rows, examOptions]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      if (assessmentFilter !== "all" && row.assessment !== assessmentFilter) return false;
      if (examFilter !== "all" && row.examTitle !== examFilter) return false;
      if (!term) return true;

      return [row.examTitle, row.assessment, row.examType, row.year, row.dateLabel, ...row.subjectNames]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [rows, assessmentFilter, examFilter, search]);

  const pageCount = Math.max(Math.ceil(filteredRows.length / rowsPerPage), 1);
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * rowsPerPage;
  const pagedRows = filteredRows.slice(pageStart, pageStart + rowsPerPage);

  useEffect(() => {
    setPage(1);
  }, [assessmentFilter, examFilter, search, rowsPerPage]);

  const renderScore = (row, size) => {
    const className = `results-page__score results-page__score--${size}`;

    if (row.graded) {
      return (
        <div className={className}>
          <p className={`results-page__score-value results-page__score-value--${row.gradeTone}`}>
            {row.grade}
          </p>
          <span className="results-page__score-label">Grade</span>
        </div>
      );
    }

    return (
      <div className={className}>
        <p className="results-page__score-value">{row.score}</p>
        <span className="results-page__score-label">Score</span>
      </div>
    );
  };

  const pageNumbers = useMemo(() => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);

    const start = Math.max(Math.min(currentPage - 3, pageCount - 6), 1);
    return Array.from({ length: 7 }, (_, i) => start + i);
  }, [pageCount, currentPage]);

  if (rLoading !== false) return <Loader />;

  return (
    <Fragment>
      <div className="results-page">
        <div className="results-page__head">
          <div>
            <h1 className="results-page__title">My Results</h1>
            <p className="results-page__subtitle">All your assessment attempts in one place</p>
          </div>
          <div className="results-page__view-toggle">
            <button
              type="button"
              className={`results-page__view-btn${view === "table" ? " is-active" : ""}`}
              onClick={() => setView("table")}
            >
              <i className="fas fa-table" /> Table
            </button>
            <button
              type="button"
              className={`results-page__view-btn${view === "cards" ? " is-active" : ""}`}
              onClick={() => setView("cards")}
            >
              <i className="fas fa-th-large" /> Cards
            </button>
          </div>
        </div>

        <div className="results-page__stats">
          <div className="results-page__stat">
            <span className="results-page__stat-icon results-page__stat-icon--green">
              <i className="fas fa-calendar-check" />
            </span>
            <div>
              <p className="results-page__stat-value">{stats.totalAttempts}</p>
              <span className="results-page__stat-label">Total Attempts</span>
            </div>
          </div>
          <div className="results-page__stat">
            <span className="results-page__stat-icon results-page__stat-icon--teal">
              <i className="fas fa-check-circle" />
            </span>
            <div>
              <p className="results-page__stat-value">{stats.examTypes}</p>
              <span className="results-page__stat-label">Exam Types</span>
            </div>
          </div>
          <div className="results-page__stat">
            <span className="results-page__stat-icon results-page__stat-icon--amber">
              <i className="fas fa-chart-line" />
            </span>
            <div>
              <p className="results-page__stat-value results-page__stat-value--text">
                {stats.mostPractised}
              </p>
              <span className="results-page__stat-label">Most Practiced</span>
            </div>
          </div>
          <div className="results-page__stat">
            <span className="results-page__stat-icon results-page__stat-icon--blue">
              <i className="fas fa-clock" />
            </span>
            <div>
              <p className="results-page__stat-value results-page__stat-value--text">
                {stats.lastAttempt}
              </p>
              <span className="results-page__stat-label">Last Attempt</span>
            </div>
          </div>
        </div>

        <div className="results-page__toolbar">
          <div className="results-page__pills">
            <button
              type="button"
              className={`results-page__pill${assessmentFilter === "all" ? " is-active" : ""}`}
              onClick={() => setAssessmentFilter("all")}
            >
              All
            </button>
            {assessmentOptions.map((option) => (
              <button
                key={option}
                type="button"
                className={`results-page__pill${assessmentFilter === option ? " is-active" : ""}`}
                onClick={() => setAssessmentFilter(option)}
              >
                {option}
              </button>
            ))}
          </div>

          <span className="results-page__toolbar-divider" />

          <select
            className="results-page__select"
            value={examFilter}
            onChange={(e) => setExamFilter(e.target.value)}
            aria-label="Filter by exam"
          >
            <option value="all">All</option>
            {examOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          <label className="results-page__search">
            <i className="fas fa-search" />
            <input
              type="search"
              value={search}
              placeholder="Search exam, year, date..."
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>

          <span className="results-page__count">{filteredRows.length} results</span>
        </div>

        {filteredRows.length === 0 ? (
          <div className="results-page__empty">
            <span className="results-page__empty-icon">
              <i className="fas fa-clipboard-list" />
            </span>
            <h2>No results to show yet</h2>
            <p>
              {rows.length === 0
                ? "Once you complete a practice session or a mock exam, your results will appear here."
                : "No attempt matches the filters you selected. Try clearing the search or filters."}
            </p>
            {rows.length === 0 && (
              <Link className="results-page__empty-btn" to="/mode-of-assessment">
                Start an assessment
              </Link>
            )}
          </div>
        ) : view === "table" ? (
          <div className="results-page__panel">
            <div className="results-page__table-scroll">
              <table className="results-page__table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Exam</th>
                    <th>Assessment</th>
                    <th>Year</th>
                    <th>Date</th>
                    <th>Score / Grade</th>
                    <th>Subject(s)</th>
                    <th>Exam Type</th>
                    <th className="results-page__cell--right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((row, index) => (
                    <tr key={row.id}>
                      <td className="results-page__cell--muted">{pageStart + index + 1}</td>
                      <td>
                        <div className="results-page__exam">
                          <span
                            className="results-page__badge"
                            style={{ backgroundColor: row.badge.accent }}
                          >
                            {row.badge.initials}
                          </span>
                          <span className="results-page__exam-title">{row.examTitle}</span>
                        </div>
                      </td>
                      <td>
                        <span className="results-page__chip">{row.assessment}</span>
                      </td>
                      <td className="results-page__cell--muted">{row.year || "—"}</td>
                      <td className="results-page__cell--muted">{row.dateLabel}</td>
                      <td>{renderScore(row, "sm")}</td>
                      <td className="results-page__cell--muted">{row.subjectLabel}</td>
                      <td>
                        <span className="results-page__chip results-page__chip--green">
                          {row.examType}
                        </span>
                      </td>
                      <td className="results-page__cell--right">
                        <Link className="results-page__action" to={row.resultUrl}>
                          View Result
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="results-page__pagination">
              <div className="results-page__rows">
                <span>Show</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  aria-label="Rows per page"
                >
                  {ROWS_PER_PAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <span>
                  rows • {filteredRows.length} total
                </span>
              </div>

              <div className="results-page__pages">
                <button
                  type="button"
                  onClick={() => setPage(1)}
                  disabled={currentPage === 1}
                  aria-label="First page"
                >
                  <i className="fas fa-angle-double-left" />
                </button>
                <button
                  type="button"
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <i className="fas fa-angle-left" />
                </button>
                {pageNumbers.map((number) => (
                  <button
                    key={number}
                    type="button"
                    className={number === currentPage ? "is-active" : ""}
                    onClick={() => setPage(number)}
                  >
                    {number}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage === pageCount}
                  aria-label="Next page"
                >
                  <i className="fas fa-angle-right" />
                </button>
                <button
                  type="button"
                  onClick={() => setPage(pageCount)}
                  disabled={currentPage === pageCount}
                  aria-label="Last page"
                >
                  <i className="fas fa-angle-double-right" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="results-page__cards">
            {filteredRows.map((row) => (
              <div key={row.id} className="results-page__card">
                <div className="results-page__card-head">
                  <span
                    className="results-page__badge"
                    style={{ backgroundColor: row.badge.accent }}
                  >
                    {row.badge.initials}
                  </span>
                  <div className="results-page__card-heading">
                    <div className="results-page__card-title">
                      <h2>{row.examTitle}</h2>
                      <span className="results-page__chip">{row.assessment}</span>
                    </div>
                    <p className="results-page__card-meta">
                      {[row.year ? `${row.year} paper` : "No specific year", row.dateLabel, row.duration]
                        .filter(Boolean)
                        .join(" • ")}
                    </p>
                    {row.subjectNames.length === 1 && (
                      <p className="results-page__card-subject">{row.subjectNames[0]}</p>
                    )}
                    {row.subjectNames.length > 1 && (
                      <p className="results-page__card-subject">{row.subjectLabel}</p>
                    )}
                  </div>
                  <i className="fas fa-check-circle results-page__card-check" />
                </div>

                <div className="results-page__card-stats">
                  {renderScore(row, "lg")}
                  <div className="results-page__score results-page__score--lg">
                    <p className="results-page__score-value">
                      {row.attemptedCount}/{row.questionCount}
                    </p>
                    <span className="results-page__score-label">Attempted</span>
                  </div>
                  <div className="results-page__score results-page__score--lg">
                    <p className="results-page__score-value results-page__score-value--blue">
                      {row.examType}
                    </p>
                    <span className="results-page__score-label">Exam Type</span>
                  </div>
                </div>

                <div className="results-page__accuracy">
                  <div className="results-page__accuracy-head">
                    <span>Accuracy</span>
                    <span
                      className={`results-page__chip results-page__chip--${row.accuracyBand.tone}`}
                    >
                      {row.accuracyBand.label}
                    </span>
                  </div>
                  <div className="results-page__bar">
                    <span
                      className={`results-page__bar-fill results-page__bar-fill--${row.accuracyBand.tone}`}
                      style={{ width: `${Math.min(row.accuracy, 100)}%` }}
                    />
                  </div>
                </div>

                <Link className="results-page__card-action" to={row.resultUrl}>
                  View Result
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default ListResults;
